import type { ReactNode } from "react"
import { Link } from "@tanstack/react-router"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type { Plan } from "@productlane/lib/src/browser"
import type { PlanTier } from "@productlane/ui"
import { PLAN_DISPLAY_NAMES } from "@productlane/lib/src/browser"
import { Button, planUpgradeBenefits } from "@productlane/ui"

import PlanHeader from "./plan-header"
import { PLAN_PRICES } from "./prices"
import { PricingContext } from "./pricing-context"

const NEXT_TIER: Record<string, PlanTier | undefined> = {
	free: "starter",
	starter: "pro",
	pro: "scale",
}

export const nextUpgradeTier = (plan: Plan): PlanTier | undefined =>
	NEXT_TIER[plan]

export function UpgradeCard({
	activePlan,
	notice,
	noticeAction,
}: {
	activePlan: Plan
	notice?: ReactNode
	noticeAction?: ReactNode
}) {
	const nextPlan = nextUpgradeTier(activePlan)
	if (!nextPlan) return null

	const from = activePlan === "free" ? "free" : (activePlan as PlanTier)
	const benefits = planUpgradeBenefits(from, nextPlan)
	const price = PLAN_PRICES[nextPlan].yearly

	return (
		<PricingContext.Provider
			value={{
				subscriptionInterval: "yearly",
				prices: PLAN_PRICES,
				isPublic: false,
				activePlan,
			}}
		>
			<div className="rounded-[10px] bg-white p-4 shadow-card sm:-mx-3.5 dark:bg-gray-850 dark:shadow-card-dark">
				{!!notice && (
					<div className="-mx-4 -mt-4 mb-4 flex items-center gap-2 rounded-t-[10px] border-b border-gray-200 bg-amber-50/70 px-4 py-2.5 text-xs text-amber-700 dark:border-gray-700/80 dark:bg-amber-400/10 dark:text-amber-400">
						<span className="h-1.5 w-1.5 flex-none rounded-full bg-amber-500" />
						<span>{notice}</span>
						{!!noticeAction && (
							<span className="ml-auto flex-none">{noticeAction}</span>
						)}
					</div>
				)}
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-col gap-1">
						<span className="text-sm font-medium text-primary">
							Upgrade to {PLAN_DISPLAY_NAMES[nextPlan]} plan
						</span>
						<span className="text-xs text-secondary">
							{price} per user/mo, billed annually
						</span>
					</div>
					<div className="flex flex-none items-center gap-2">
						<Link to="/settings/plans/pricing">
							<Button variant="ghost">View all plans</Button>
						</Link>
						<PlanHeader.CheckoutButton plan={nextPlan} />
					</div>
				</div>

				<div className="mt-4 columns-1 gap-x-6 border-t border-gray-200 pt-4 sm:columns-2 lg:columns-3 dark:border-gray-700/80">
					{benefits.map((benefit) => (
						<div
							key={benefit.label}
							className="flex break-inside-avoid items-start gap-2 pb-2 text-sm text-secondary"
						>
							<Check className="mt-px h-4 w-4 flex-none text-primary" />
							<span className="leading-snug">
								{benefit.label}
								{benefit.detail && (
									<span className="text-tertiary"> {benefit.detail}</span>
								)}
							</span>
						</div>
					))}
				</div>
			</div>
		</PricingContext.Provider>
	)
}
